import { useState } from 'react';

import {
  AccountViewListingGroupType,
  AccountViewListingStatus,
  PropertyType,
} from '@zg-rentals/ts-rental-manager-api-beta-client';
import { Box, Button, Table } from '@zillow/constellation-10';

import { transformFiltersToApiParams } from '../../../hooks/queries/zram';
import { useGetMultiplexUnits } from '../../../hooks/queries/properties/useGetMultiplexUnits';
import { useSelector } from '../../../selectors/useSelector';

import GroupablePropertyRow from '../GroupablePropertyRow';

import type {
  AccountViewListingGroupDto,
  AccountViewMultiplexUnitDto,
  WebAnalyticsLeaseDto,
} from '@zg-rentals/ts-rental-manager-api-beta-client';

import type { ZrmClickstreamListing } from '@zg-rentals/clickstream';

type GroupableHeaderRowProps = {
  listing: AccountViewListingGroupDto;
  showMoreListingAlias: string;
  setShowMoreListingAlias: (alias: string) => void;
  onUpsellCellHover: (
    columnLabel: string,
    currentProperty: ZrmClickstreamListing,
    lease?: WebAnalyticsLeaseDto,
  ) => void;
  isScrolledToLeft: boolean;
  filters: { searchText: string; subNavFilterType: string };
};

const MULTIPLEX_UNIT_SKELETON_ROW_COUNT = 3;

const emptyListingCount = {
  active: 0,
  archived: 0,
  atRisk: 0,
  freeBonusListingCount: 0,
  freeClaimedListingCount: 0,
  freeListingsOwnedByCustomer: 0,
  inactive: 0,
  total: 0,
};

const emptyAddress = { city: '', state: '', street: '', unit: '', zip: '' };

// placeholder listing for multiplex unit skeleton rows
const multiplexSkeletonPlaceholderListing = (parentGroupId: string): AccountViewListingGroupDto => ({
  type: AccountViewListingGroupType.LISTING,
  groupId: parentGroupId,
  listingAlias: '',
  name: '',
  photoKey: '',
  propertyType: PropertyType.UNKNOWN,
  status: {
    status: AccountViewListingStatus.OFF_MARKET,
    tagAppearance: '',
    text: '',
  },
  isMultifamily: false,
  address: emptyAddress,
  listingCount: emptyListingCount,
});

const mapUnitToListingGroupRow = (unit: AccountViewMultiplexUnitDto): AccountViewListingGroupDto => {
  return {
    type: AccountViewListingGroupType.LISTING,
    groupId: unit.groupId,
    listingAlias: unit.listingAlias,
    name: unit.name,
    photoKey: unit.photoKey,
    propertyType: PropertyType.UNKNOWN,
    status: unit.status,
    isMultifamily: false,
    address: unit.address,
    listingCount: emptyListingCount,
  };
};

const getColSpan = (constellationScreenSize: string) => {
  switch (constellationScreenSize) {
    case 'lg':
      return 5;
    case 'xl':
      return 5;
    case 'xxl':
      return 5;
    default:
      return 2;
  }
};

const GroupableHeaderRow = ({
  listing,
  showMoreListingAlias,
  setShowMoreListingAlias,
  onUpsellCellHover,
  isScrolledToLeft,
  filters,
}: GroupableHeaderRowProps) => {
  const constellationScreenSize = useSelector((state) => state.app.constellationScreenSize);

  const [isExpanded, setIsExpanded] = useState(false);

  const apiParams = transformFiltersToApiParams(filters);
  const groupId = listing.groupId;

  const {
    dataByGroupId,
    fetchNextPageForGroup,
    hasNextPageByGroupId,
    isFetchingByGroupId,
    isFetchingNextPageByGroupId,
  } = useGetMultiplexUnits(apiParams, isExpanded ? [groupId] : []);

  const unitsData = dataByGroupId[groupId];
  const units = unitsData?.units || [];
  const hasNextPage = hasNextPageByGroupId[groupId] || false;
  const isFetching = isFetchingByGroupId[groupId] || false;
  const isFetchingNextPage = isFetchingNextPageByGroupId[groupId] || false;

  const showMultiplexUnitSkeletons = isFetching && units.length === 0;

  const handleExpandClick = () => {
    setIsExpanded(!isExpanded);
  };

  const handleLoadMore = () => {
    const endKey = unitsData?.pager?.endKey;

    if (endKey && fetchNextPageForGroup) {
      fetchNextPageForGroup(groupId, endKey);
    }
  };

  return (
    <>
      {/* parent row */}
      <GroupablePropertyRow
        data-testid="GroupableHeaderRow"
        index={0}
        listing={listing}
        isLoading={false}
        showMoreListingAlias={showMoreListingAlias}
        setShowMoreListingAlias={setShowMoreListingAlias}
        onUpsellCellHover={onUpsellCellHover}
        isScrolledToLeft={isScrolledToLeft}
        onExpandGroupClick={handleExpandClick}
        isRowOpen={isExpanded}
        isFetching={isFetching}
      />

      {/* skeleton rows for loading state */}
      {isExpanded &&
        showMultiplexUnitSkeletons &&
        Array.from({ length: MULTIPLEX_UNIT_SKELETON_ROW_COUNT }, (_, i) => (
          <GroupablePropertyRow
            key={`multiplex-unit-skeleton-${groupId}-${i}`}
            index={i}
            listing={multiplexSkeletonPlaceholderListing(groupId)}
            isLoading
            showMoreListingAlias={showMoreListingAlias}
            setShowMoreListingAlias={setShowMoreListingAlias}
            onUpsellCellHover={onUpsellCellHover}
            isScrolledToLeft={isScrolledToLeft}
          />
        ))}

      {/* unit rows */}
      {isExpanded &&
        !showMultiplexUnitSkeletons &&
        units.map((unit, i) => (
          <GroupablePropertyRow
            key={`unit-${groupId}-${unit.listingAlias}`}
            index={i}
            listing={mapUnitToListingGroupRow(unit)}
            showMoreListingAlias={showMoreListingAlias}
            setShowMoreListingAlias={setShowMoreListingAlias}
            onUpsellCellHover={onUpsellCellHover}
            isScrolledToLeft={isScrolledToLeft}
          />
        ))}

      {/* load more units button */}
      {isExpanded && hasNextPage && (
        <Table.Row
          key={`loadMore-${groupId}`}
          data-testid="GroupableHeaderRow-LoadMoreUnitsRow"
          css={{ backgroundColor: '{colors.bg.softest}' }}
        >
          <Table.Cell
            colSpan={getColSpan(constellationScreenSize)}
            css={{
              padding: '400',
              textAlign: 'center',
            }}
          >
            <Box css={{ display: 'flex', justifyContent: 'center', width: '100%' }}>
              <Button tone="brand" emphasis="outlined" size="sm" onClick={handleLoadMore} loading={isFetchingNextPage}>
                Load more units
              </Button>
            </Box>
          </Table.Cell>
        </Table.Row>
      )}
    </>
  );
};

export { GroupableHeaderRow };
export type { GroupableHeaderRowProps };
export default GroupableHeaderRow;
